import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CssBaseline } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import { AppSettingsProvider } from './contexts/AppSettingsContext';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Auth Pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';

// Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard';

// Admin Pages
import AssociationsList from './pages/admin/AssociationsList';
import AssociationForm from './pages/admin/AssociationForm';
import UsersList from './pages/admin/UsersList';
import UserForm from './pages/admin/UserForm';
import DistributorActivity from './pages/admin/DistributorActivity';

// Customer Pages
import CustomersList from './pages/customers/CustomersList';
import CustomerDetail from './pages/customers/CustomerDetail';

// Upload Pages
import OutstandingUpload from './pages/uploads/OutstandingUpload';
import UploadHistory from './pages/uploads/UploadHistory';

// Reports Pages have been removed

// Status Pages
import StatusChangesList from './pages/status/StatusChangesList';

// User Settings
import UserProfile from './pages/settings/UserProfile';
import ChangePassword from './pages/settings/ChangePassword';

// Error Pages
import NotFound from './pages/errors/NotFound';
import Unauthorized from './pages/errors/Unauthorized';

// Private Route Component
const PrivateRoute = ({ element, requiredRoles = [] }) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If requiredRoles is empty or user role is included, render the component
  if (
    requiredRoles.length === 0 || 
    requiredRoles.includes(user?.role) ||
    (requiredRoles.includes('association_admin') && user?.is_association_admin)
  ) {
    // If element is a function, pass user as prop
    return typeof element === 'function' ? element({ user }) : element;
  }

  // If user doesn't have the required role, redirect to unauthorized
  return <Navigate to="/unauthorized" replace />;
};

function App() {
  const { isAuthenticated, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    // Redirect to appropriate page if already logged in and on login page
    if (isAuthenticated && location.pathname === '/login') {
      // Check user role from localStorage
      const userStr = localStorage.getItem('user');
      let user = null;
      try {
        if (userStr) {
          user = JSON.parse(userStr);
        }
      } catch (e) {
        console.error('Error parsing user data', e);
      }

      // Redirect viewers to customers page, others to dashboard
      if (user && user.role === 'viewer') {
        navigate('/customers');
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, navigate, location]);

  return (
    <>
      <CssBaseline />
      <ToastContainer position="top-right" autoClose={5000} />
      
      <AppSettingsProvider>
        <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>

        {/* App Routes */}
        <Route element={<MainLayout />}>
          {/* Dashboard & Root Route */}
          <Route path="/" element={
            <PrivateRoute 
              element={
                ({ user }) => {
                  // Redirect based on user role
                  if (user?.role === 'viewer') {
                    return <Navigate to="/customers" replace />;
                  } else {
                    return <Navigate to="/dashboard" replace />;
                  }
                }
              } 
            />
          } />
          <Route 
            path="/dashboard" 
            element={<PrivateRoute element={<Dashboard />} requiredRoles={['superadmin', 'subadmin']} />} 
          />

          {/* SuperAdmin Routes */}
          <Route
            path="/admin/associations"
            element={<PrivateRoute element={<AssociationsList />} requiredRoles={['superadmin']} />}
          />
          <Route
            path="/admin/associations/new"
            element={<PrivateRoute element={<AssociationForm />} requiredRoles={['superadmin']} />}
          />
          <Route
            path="/admin/associations/:id/edit"
            element={<PrivateRoute element={<AssociationForm />} requiredRoles={['superadmin']} />}
          />
          <Route
            path="/admin/users"
            element={<PrivateRoute element={<UsersList />} requiredRoles={['superadmin', 'association_admin']} />}
          />
          <Route
            path="/admin/users/new"
            element={<PrivateRoute element={<UserForm />} requiredRoles={['superadmin', 'association_admin']} />}
          />
          <Route
            path="/admin/users/:id/edit"
            element={<PrivateRoute element={<UserForm />} requiredRoles={['superadmin', 'association_admin']} />}
          />
          <Route
            path="/admin/distributor-activity"
            element={<PrivateRoute element={<DistributorActivity />} requiredRoles={['superadmin', 'association_admin', 'subadmin']} />}
          />

          {/* Customer Routes */}
          <Route 
            path="/customers" 
            element={<PrivateRoute element={<CustomersList />} />} 
          />
          <Route 
            path="/customers/:id" 
            element={<PrivateRoute element={<CustomerDetail />} />} 
          />

          {/* Upload Routes */}
          <Route 
            path="/uploads/outstanding" 
            element={<PrivateRoute element={<OutstandingUpload />} requiredRoles={['subadmin', 'viewer']} />} 
          />
          <Route 
            path="/uploads/history" 
            element={<PrivateRoute element={<UploadHistory />} />} 
          />

          {/* Reports Routes have been removed */}

          {/* Status Routes */}
          <Route 
            path="/status-changes" 
            element={<PrivateRoute element={<StatusChangesList />} requiredRoles={['subadmin', 'association_admin']} />} 
          />
          
          {/* Viewer Management for SubAdmins */}
          <Route 
            path="/viewers" 
            element={<PrivateRoute element={<UsersList viewersOnly={true} />} requiredRoles={['subadmin']} />} 
          />
          <Route 
            path="/viewers/new" 
            element={<PrivateRoute element={<UserForm viewerOnly={true} />} requiredRoles={['subadmin']} />} 
          />
          <Route 
            path="/viewers/:id/edit" 
            element={<PrivateRoute element={<UserForm viewerOnly={true} />} requiredRoles={['subadmin']} />} 
          />

          {/* User Settings */}
          <Route 
            path="/profile" 
            element={<PrivateRoute element={<UserProfile />} />} 
          />
          <Route 
            path="/change-password" 
            element={<PrivateRoute element={<ChangePassword />} />} 
          />

          {/* Error Pages */}
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      </AppSettingsProvider>
    </>
  );
}

export default App;